import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload } from 'lucide-react';
import {
  UNITS,
  unitLabel,
  type SheetShapeDto,
  type SupplyPreviewDto,
  type Unit,
} from '@eombor/contracts';
import { Badge, Button, Dialog, Input, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { unitAliasApi } from '../../catalog/api/unit-alias.api.js';
import { supplyApi } from '../api/supply.api.js';

/** Ekranda ko'rsatiladigan satr soni — qolgani "va yana N ta" bo'lib qisqaradi. */
const PREVIEW_ROWS = 12;

export interface SupplyUploadDialogProps {
  open: boolean;
  onClose: () => void;
  warehouses: { id: string; name: string; code: string }[];
  onUploaded: () => void;
}

const errorText = (error: unknown, fallback: string): string =>
  error instanceof ApiClientError ? error.message : fallback;

const qty = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, '');

/**
 * 1C MODDIY HISOBOTINI YUKLASH — buxgalter uchun.
 *
 * IKKI QADAM va tartibi ataylab: avval fayl O'QIB KO'RSATILADI, keyin
 * yuboriladi. Ko'rmasdan yuborilgan 233 satrli fayl keyin qo'lda
 * tuzatiladigan ishga aylanardi; bu yerda esa buxgalter yuborishdan oldin
 * nechta yangi material paydo bo'lishini va qayerda xato borligini ko'radi.
 *
 * Xato bo'lsa "Yuborish" ochilmaydi — backend ham uni rad etadi
 * (`supply.service.ts`), lekin sababni ekranda ko'rsatish tugmani jimgina
 * o'chirib qo'yishdan yaxshiroq.
 */
export const SupplyUploadDialog = ({
  open,
  onClose,
  warehouses,
  onUploaded,
}: SupplyUploadDialogProps) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<SupplyPreviewDto | null>(null);

  const read = useMutation({
    mutationFn: (next: File) => supplyApi.preview(next),
    onSuccess: setPreview,
  });

  const send = useMutation({
    mutationFn: () => supplyApi.create(warehouseId, file as File, note.trim()),
    onSuccess: () => {
      onUploaded();
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setNote('');
    setWarehouseId(warehouses.length === 1 ? (warehouses[0]?.id ?? '') : '');
    read.reset();
    send.reset();
    if (fileRef.current !== null) fileRef.current.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutatsiyalar har chizishda yangi
  }, [open]);

  const pick = (next: File | null): void => {
    setFile(next);
    setPreview(null);
    read.reset();
    send.reset();
    if (next !== null) read.mutate(next);
  };

  const hasErrors = preview !== null && preview.errors.length > 0;
  /* TAKROR fayl to'siq: ikkinchi hujjat o'sha satrlarni qayta taklif
     qiladi va bir partiya ikki marta kataklarga tushib ketishi mumkin.
     Server ham rad etadi — bu yerdagi to'siq faqat sababini oldindan
     aytish uchun. */
  const isDuplicate = preview !== null && preview.duplicateOf !== null;
  const canSend =
    file !== null && preview !== null && !hasErrors && !isDuplicate && warehouseId !== '';

  /* Yig'indi fayldagi «Итого» bilan tushmasa — fayl to'liq o'qilmagan
     bo'lishi mumkin. Bu to'siq emas, OGOHLANTIRISH: 1C ba'zan yakunni
     boshqacha yaxlitlaydi. */
  const totalMismatch =
    preview !== null &&
    preview.sheetTotalQty !== null &&
    Math.abs(preview.sheetTotalQty - preview.totalBalanceQty) > 0.005;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t('supply.uploadTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('supply.cancel')}
          </Button>
          <Button loading={send.isPending} disabled={!canSend} onClick={() => send.mutate()}>
            <Upload size={16} aria-hidden />
            {t('supply.send')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-text-muted text-sm text-pretty">{t('supply.uploadHint')}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('supply.warehouse')}
            placeholder={t('supply.warehousePlaceholder')}
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
            options={warehouses.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
          />
          <Input
            label={t('supply.note')}
            hint={t('supply.noteHint')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <label
          className={cn(
            'border-border-control hover:border-accent/50 flex cursor-pointer items-center gap-3',
            'rounded-[var(--radius-sm)] border border-dashed px-4 py-4 transition-colors',
          )}
        >
          <FileSpreadsheet size={22} aria-hidden className="text-text-faint shrink-0" />
          <span className="flex min-w-0 flex-col">
            <span className="text-text text-sm font-medium">
              {file?.name ?? t('supply.pickFile')}
            </span>
            <span className="text-text-faint text-xs">{t('supply.pickFileHint')}</span>
          </span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="sr-only"
            onChange={(event) => pick(event.target.files?.[0] ?? null)}
          />
        </label>

        {read.isPending ? <p className="text-text-muted text-sm">{t('supply.reading')}</p> : null}

        {read.error === null || read.error === undefined ? null : (
          <p
            role="alert"
            className="border-danger/25 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
          >
            {errorText(read.error, t('supply.readFailed'))}
          </p>
        )}

        {preview === null ? null : (
          <PreviewPanel
            preview={preview}
            totalMismatch={totalMismatch}
            onUnitsMapped={() => {
              if (file !== null) read.mutate(file);
            }}
          />
        )}

        {send.error === null || send.error === undefined ? null : (
          <p
            role="alert"
            className="border-danger/25 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm text-pretty"
          >
            {errorText(send.error, t('supply.sendFailed'))}
          </p>
        )}
      </div>
    </Dialog>
  );
};

/**
 * FAYL QANDAY TUSHUNILGANI — xatolardan OLDIN.
 *
 * «Sarlavha topilmadi» yoki «Miqdor ustunlari topilmadi» xabari to'g'ri,
 * lekin odamga nimani tuzatishni aytmaydi: u faylni ochib, parserning
 * nimasi yoqmaganini taxmin qilishga majbur. Shuning uchun bu panel
 * parser KO'RGANINI ko'rsatadi — qaysi ustun nima deb olindi, jadval
 * tanilmagan bo'lsa esa faylda umuman qanday sarlavhalar borligini.
 * Sabab shu bilan ekranga chiqadi va qo'ng'iroq qilish kerak bo'lmaydi.
 */
const ShapePanel = ({ shape }: { shape: SheetShapeDto }) => {
  const unknown = shape.kind === 'UNKNOWN';
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded-[var(--radius-sm)] border px-3 py-2.5 text-sm',
        unknown ? 'border-warn/30 bg-warn-soft' : 'border-border bg-surface-2',
      )}
    >
      <span className={cn('font-medium', unknown ? 'text-warn' : 'text-text')}>
        {unknown ? t('supply.shapeUnknown') : t('supply.shapeTitle')}
        {shape.headerRow === null
          ? null
          : ` · ${t('supply.shapeHeaderRow', { row: String(shape.headerRow) })}`}
        {unknown ? null : ` · ${t(shape.kind === 'BALANCE' ? 'supply.shapeBalance' : 'supply.shapeReport')}`}
      </span>

      {shape.columns.length === 0 ? null : (
        <ul className="text-text-muted flex flex-col gap-0.5 text-xs">
          {shape.columns.map((column) => (
            <li key={column.field}>
              {t(`supply.field${column.field.charAt(0).toUpperCase()}${column.field.slice(1)}`)}
              {' ← '}
              <span className="text-text font-mono">«{column.label}»</span>
            </li>
          ))}
        </ul>
      )}

      {/* Jadval tanilmaganda eng foydali narsa — faylda NIMA borligi. */}
      {!unknown || shape.seen.length === 0 ? null : (
        <p className="text-text-muted text-xs text-pretty">
          <span className="font-medium">{t('supply.shapeSeen')}:</span>{' '}
          <span className="font-mono">{shape.seen.join(' · ')}</span>
        </p>
      )}

      <p className="text-text-faint text-xs">{t('supply.shapeHint')}</p>
    </div>
  );
};

/** O'qilgan faylning xulosasi — yuborishdan OLDIN ko'riladigan yagona joy. */
const PreviewPanel = ({
  preview,
  totalMismatch,
  onUnitsMapped,
}: {
  preview: SupplyPreviewDto;
  totalMismatch: boolean;
  /** Birlik mos keltirilgach fayl QAYTA o'qiladi — xato o'z-o'zidan yo'qoladi. */
  onUnitsMapped: () => void;
}) => (
  <div className="flex flex-col gap-3">
    <ShapePanel shape={preview.shape} />

    <div className="border-border bg-surface-2 grid gap-3 rounded-[var(--radius-sm)] border px-4 py-3 sm:grid-cols-2">
      {preview.periodLabel === null ? null : (
        <Row label={t('supply.period')} value={preview.periodLabel} />
      )}
      {preview.sourceLabel === null ? null : (
        <Row label={t('supply.source')} value={preview.sourceLabel} />
      )}
      <Row label={t('supply.lineCount')} value={String(preview.lineCount)} />
      <Row label={t('supply.newCodes')} value={String(preview.newCodeCount)} />
      <Row label={t('supply.totalQty')} value={qty(preview.totalBalanceQty)} />
    </div>

    {totalMismatch ? (
      <p className="border-warn/30 bg-warn-soft text-warn flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm">
        <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
        <span className="text-pretty">
          {t('supply.totalMismatch', {
            sheet: qty(preview.sheetTotalQty ?? 0),
            read: qty(preview.totalBalanceQty),
          })}
        </span>
      </p>
    ) : null}

    {preview.duplicateOf === null ? null : (
      <p
        role="alert"
        className="border-danger/25 bg-danger-soft text-danger flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
      >
        <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
        <span className="text-pretty">
          {t('supply.duplicate', { docNo: preview.duplicateOf.docNo })}
        </span>
      </p>
    )}

    {/* Eslatma — to'siq EMAS: satr o'qildi, lekin buxgalter bilishi kerak
        (masalan, 1C da kodi yo'q materialga kod nomidan berildi). */}
    {preview.warnings.length === 0 ? null : (
      <div className="border-warn/30 bg-warn-soft flex flex-col gap-1.5 rounded-[var(--radius-sm)] border px-3 py-2.5">
        <span className="text-warn flex items-center gap-2 text-sm font-medium">
          <AlertTriangle size={16} aria-hidden className="shrink-0" />
          {t('supply.warningCount', { count: preview.warnings.length })}
        </span>
        {preview.warnings.map((warning) => (
          <span key={`${warning.sheetRow}-${warning.message}`} className="text-warn text-sm">
            {t('supply.errorRow', { row: warning.sheetRow })} — {warning.message}
          </span>
        ))}
      </div>
    )}

    {preview.unknownUnits.length === 0 ? null : (
      <UnknownUnits units={preview.unknownUnits} onMapped={onUnitsMapped} />
    )}

    {preview.errors.length > 0 ? (
      <div
        role="alert"
        className="border-danger/25 bg-danger-soft flex flex-col gap-1.5 rounded-[var(--radius-sm)] border px-3 py-2.5"
      >
        <span className="text-danger text-sm font-medium">
          {t('supply.errorCount', { count: preview.errors.length })}
        </span>
        {preview.errors.slice(0, 6).map((error) => (
          <span key={`${error.sheetRow}-${error.message}`} className="text-danger text-sm">
            {t('supply.errorRow', { row: error.sheetRow })} — {error.message}
          </span>
        ))}
        {preview.errors.length > 6 ? (
          <span className="text-danger text-xs">
            {t('supply.andMore', { count: preview.errors.length - 6 })}
          </span>
        ) : null}
      </div>
    ) : (
      <p className="text-ok flex items-center gap-2 text-sm">
        <CheckCircle2 size={16} aria-hidden />
        {t('supply.readOk')}
      </p>
    )}

    {preview.lines.length === 0 ? null : (
      <div className="border-border overflow-x-auto rounded-[var(--radius-sm)] border">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-text-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">{t('supply.colCode')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('supply.colName')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('supply.colQty')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('supply.colUnit')}</th>
            </tr>
          </thead>
          <tbody>
            {preview.lines.slice(0, PREVIEW_ROWS).map((line) => (
              <tr key={line.sheetRow} className="border-border border-t">
                <td className="tabular text-text px-3 py-1.5 whitespace-nowrap">
                  <span className="flex items-center gap-2">
                    {line.code}
                    {/* Yangi material — katalogda hali yo'q, tasdiqlashda yaratiladi. */}
                    {line.isNew ? <Badge tone="info">{t('supply.new')}</Badge> : null}
                  </span>
                </td>
                <td className="text-text max-w-[22rem] truncate px-3 py-1.5">{line.name}</td>
                <td className="tabular text-text px-3 py-1.5 text-right">{qty(line.balanceQty)}</td>
                <td className="text-text-muted px-3 py-1.5 whitespace-nowrap">{line.unitRaw}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {preview.lines.length > PREVIEW_ROWS ? (
          <p className="text-text-faint border-border border-t px-3 py-2 text-xs">
            {t('supply.andMore', { count: preview.lines.length - PREVIEW_ROWS })}
          </p>
        ) : null}
      </div>
    )}
  </div>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <span className="flex min-w-0 flex-col gap-0.5">
    <span className="text-text-faint text-xs">{label}</span>
    <span className="text-text truncate text-sm font-medium">{value}</span>
  </span>
);

/**
 * TANILMAGAN BIRLIK — shu yerda, shu daqiqada hal qilinadi.
 *
 * Ilgari bu xato dasturchiga borardi: 1C birlikni erkin matn bilan
 * yozadi (`шт`, `дона(кадок)`, `шт (упаковка)`) va har yangi ko'rinish
 * kodga qo'lda qo'shilardi — ya'ni beshta bo'yoq satri tufayli 148
 * satrlik hisobot ertagacha kutib turardi.
 *
 * Endi buxgalter birlikning MA'NOSINI aytadi: «дона(кадок) — bu dona».
 * Javob bazaga yoziladi va boshqa hech qachon so'ralmaydi, ya'ni keyingi
 * oyning hisoboti o'z-o'zidan o'tadi.
 *
 * Yangi birlik YARATILMAYDI, faqat mavjudiga ulanadi: birliklar ro'yxati
 * qisqa va unga qoidalar osilgan (yarim dona bo'lmaydi, etiketkada
 * yorlig'i chiqadi).
 */
const UnknownUnits = ({
  units,
  onMapped,
}: {
  units: SupplyPreviewDto['unknownUnits'];
  onMapped: () => void;
}) => {
  const [picked, setPicked] = useState<Record<string, Unit | ''>>({});
  const [failed, setFailed] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (raw: string) => {
      const unit = picked[raw];
      if (unit === undefined || unit === '') return;
      await unitAliasApi.create({ raw, unit });
    },
    onSuccess: () => {
      setFailed(null);
      onMapped();
    },
    onError: (error: unknown) => {
      setFailed(errorText(error, t('supply.unitSaveFailed')));
    },
  });

  return (
    <div className="border-accent/30 bg-accent-soft flex flex-col gap-2.5 rounded-[var(--radius-sm)] border px-3 py-2.5">
      <span className="text-text flex items-start gap-2 text-sm font-medium">
        <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
        <span className="text-pretty">{t('supply.unknownUnitTitle')}</span>
      </span>
      <p className="text-text-muted text-xs text-pretty">{t('supply.unknownUnitHint')}</p>

      {units.map((item) => (
        <div key={item.raw} className="flex flex-wrap items-end gap-2">
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-text truncate text-sm font-medium">«{item.raw}»</span>
            <span className="text-text-faint text-xs">
              {t('supply.unknownUnitRows', { count: item.rowCount, row: item.sheetRow })}
              {item.sampleName === null ? '' : ` · ${item.sampleName}`}
            </span>
          </span>

          <Select
            label={t('supply.unknownUnitPick')}
            hiddenLabel
            placeholder={t('supply.unknownUnitPick')}
            className="w-36"
            value={picked[item.raw] ?? ''}
            onChange={(event) =>
              setPicked((current) => ({ ...current, [item.raw]: event.target.value as Unit }))
            }
            options={UNITS.map((unit) => ({ value: unit, label: unitLabel(unit) }))}
          />

          <Button
            size="sm"
            variant="secondary"
            disabled={(picked[item.raw] ?? '') === ''}
            loading={save.isPending && save.variables === item.raw}
            onClick={() => save.mutate(item.raw)}
          >
            {t('supply.unitSave')}
          </Button>
        </div>
      ))}

      {failed === null ? null : (
        <p role="alert" className="text-danger text-sm">
          {failed}
        </p>
      )}
    </div>
  );
};
